import L from "leaflet";
import "leaflet/dist/leaflet.css";
import mustache from "mustache";
import { GOOGLE_MAPS_API_KEY } from "../../../../../../constantes";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { MapZoomButton } from "../../../../implementations/map/MapZoomButton";
import { OrtomosaicoControl } from "../../../../implementations/map/OrtomosaicoControl";
import { LoteAssignBarRender } from "./LoteAssignBarRender";
import { LoteAssignEntry, LoteAssignState } from "./LoteAssignState";
import template from "./loteAssignMapTemplate.html";

mustache.parse(template);

let LOTE_ASSIGN_MAP_ID = 0;

/** Resalte de los lotes SELECCIONADOS: azul aguamarina, `#40E0D0`.
 *
 *  **Es EXACTAMENTE el mismo color de la herramienta Dividir del paso 4**
 *  (`CONTORNO_A_DIVIDIR` en `LoteSplitToolbar`), borde y relleno, sin variantes
 *  ni tonos derivados: pedido explícito, para que seleccionar un lote se vea
 *  igual en los dos pasos.
 *
 *  Acá se intentó oscurecer el borde (`#1b9a8f`) para que se separara del
 *  relleno, y es justamente lo que no se quiere — un tono inventado deja de ser
 *  el color del sistema. El contraste lo da la opacidad: el relleno va al 55% y
 *  el borde al 100%.
 *
 *  Antes este resalte era amarillo (`#f5c542` con borde `#b8860b`), que en este
 *  paso competía con el verde del productivo y el naranja del no productivo. */
const SELECTED_BORDER = "#40E0D0";
const SELECTED_FILL = "#40E0D0";
/** Los tres estados en que puede estar un lote en este paso. SIN ASIGNAR es
 *  un estado propio, no "no productivo": antes los dos se pintaban igual
 *  porque en el modelo comparten `isAreaProductiva === false`, y no se
 *  distinguia lo que faltaba por asignar de lo ya marcado. */
const PRODUCTIVO_COLOR = "#3cb44b";
const NO_PRODUCTIVO_COLOR = "rgb(224, 73, 13)";
const SIN_ASIGNAR_COLOR = "#ffffff";

const TOOLTIP_TEMPLATE =
  '<strong>{{nombre}}</strong><br/><span class="lote-assign-tooltip-sub">{{sub}}</span>';
mustache.parse(TOOLTIP_TEMPLATE);

/** Mapa del paso 5 (asignar tipo/cultivo) — mismo patrón liviano que
 *  `LoteSplitMapRender` (standalone, no extiende `BaseMapRender`), arranca
 *  encuadrado a los lotes. A diferencia de paso 4, acá no se dibuja nada:
 *  click en un lote togglea su selección en `LoteAssignState`
 *  (multi-select), y el color refleja el estado de asignación: blanco sin
 *  asignar, verde productivo, naranja no productivo (dato
 *  real, sin ambigüedad, a diferencia del contador de "asignados" del
 *  panel de indicaciones). Monta `LoteAssignBarRender` como overlay (mismo
 *  mecanismo que `AoiSearchBarRender` sobre `AoiMapRender`). */
export class LoteAssignMapRender implements IConnectRender {
  private readonly _mapId: string;
  private _map: L.Map | null = null;
  private _ortomosaico: OrtomosaicoControl | null = null;
  private _zoomIn = new MapZoomButton("ph-plus", "Acercar", (m) => m.zoomIn());
  private _zoomOut = new MapZoomButton("ph-minus", "Alejar", (m) => m.zoomOut());
  private _bar = new LoteAssignBarRender();
  private _lotesLayerGroup = new L.FeatureGroup();

  /** `conOrtomosaico`: mismo criterio que el paso 4 — sólo en modo EDICIÓN, que
   *  es cuando el predio ya existe y puede tener uno cargado. */
  constructor(
    private readonly _state: LoteAssignState,
    private readonly _isAgricultura: boolean,
    private readonly _conOrtomosaico = false,
  ) {
    LOTE_ASSIGN_MAP_ID += 1;
    this._mapId = LOTE_ASSIGN_MAP_ID.toString();
  }

  render(): string {
    return mustache.render(template, { mapId: this._mapId });
  }

  connect(): void {
    const container = document.getElementById(
      `loteAssignMapInstance-${this._mapId}`,
    );
    if (!container) return;

    this._map = L.map(container, {
      zoomControl: false,
      preferCanvas: true,
    });

    L.tileLayer(
      `https://mt{s}.google.com/vt?lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_API_KEY}`,
      { maxZoom: 20, subdomains: ["0", "1", "2", "3"] },
    ).addTo(this._map);

    this._lotesLayerGroup.addTo(this._map);
    this._state.addOnChangeListener(this._renderLotes);

    const mapControlsRight = document.getElementById(
      `loteAssignMapControlsRight-${this._mapId}`,
    );
    if (mapControlsRight) {
      this._zoomIn.init(mapControlsRight, this._map);
      this._zoomOut.init(mapControlsRight, this._map);
      if (this._conOrtomosaico) {
        this._ortomosaico = new OrtomosaicoControl();
        // Con label: acá el botón comparte columna con las herramientas del paso,
        // que llevan todas su texto.
        this._ortomosaico.init(mapControlsRight, this._map, true);
      }
    }

    const barWrap = document.getElementById(`loteAssignBarWrap-${this._mapId}`);
    if (barWrap) {
      barWrap.innerHTML = this._bar.render();
      this._bar.connect(this._state, this._isAgricultura);
    }

    this._renderLotes();
    this._fitToLotes();
    this._map.invalidateSize();

    // Leaflet mide el contenedor UNA vez, al construir el mapa, y cachea ese
    // tamano. El `invalidateSize()` de arriba corrige la primera medicion,
    // cuando el layout del paso todavia no estaba asentado, pero no sirve
    // para nada posterior: al redimensionar la ventana, al abrirse el panel
    // lateral o al reflowear la barra de asignacion, el mapa se quedaba
    // pintado con el ancho viejo y aparecia la franja gris del fondo default
    // de Leaflet. El observer lo remide cada vez que el contenedor cambia.
    this._resizeObserver = new ResizeObserver(() => this._map?.invalidateSize());
    this._resizeObserver.observe(container);
  }

  private _resizeObserver: ResizeObserver | null = null;

  dispose(): void {
    this._ortomosaico?.dispose();
    this._ortomosaico = null;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._zoomIn.dispose();
    this._zoomOut.dispose();
    this._bar.dispose();
    this._map?.remove();
    this._map = null;
  }

  private _styleFor(entry: LoteAssignEntry): L.PathOptions {
    if (this._state.isSelected(entry.id)) {
      return {
        color: SELECTED_BORDER,
        weight: 3,
        opacity: 1,
        fillColor: SELECTED_FILL,
        fillOpacity: 0.55,
      };
    }
    const fillColor = !this._state.isTocado(entry.id)
      ? SIN_ASIGNAR_COLOR
      : entry.isAreaProductiva
        ? PRODUCTIVO_COLOR
        : NO_PRODUCTIVO_COLOR;
    return { color: fillColor, weight: 2, opacity: 1, fillColor, fillOpacity: 0.35 };
  }

  /** Segunda línea del tooltip: lo que el lote TIENE asignado, en los mismos
   *  tres estados que el color (`_styleFor`).
   *   - Sin asignar: su propio texto. No cae en "No productivo" porque en el
   *     modelo comparte `isAreaProductiva === false` con un no productivo de
   *     verdad, y el mapa diría algo que el usuario nunca eligió.
   *   - Productivo: el nombre del CULTIVO elegido. El genérico "Área
   *     productiva" queda sólo como respaldo para cuando no hay nombre: lotes
   *     que vuelven del borrador sin tocarse en esta sesión
   *     (`LoteAssignState.init` arranca `cultivoNombre` en null).
   *   - No productivo: **"No productivo", siempre**, sin nombrar la cobertura.
   *     Pedido explícito. La cobertura se sigue eligiendo y se sigue guardando
   *     igual (`coberturaId`, ver `LoteAssignBarRender`): lo único que cambia es
   *     la etiqueta del mapa. Acá estuvo mostrándose el nombre de la cobertura
   *     —viene en `cultivoNombre`, que para un no productivo sale del catálogo
   *     de coberturas— y en el mapa lo que importa de estos lotes es que no
   *     producen, no con qué están cubiertos. */
  private _tooltipFor(entry: LoteAssignEntry): string {
    const sub = !this._state.isTocado(entry.id)
      ? "Sin asignar"
      : entry.isAreaProductiva
        ? (entry.cultivoNombre ?? "Área productiva")
        : "No productivo";
    // Mustache escapa por defecto: `nombreLote` lo escribe el usuario en el
    // paso 4 y el tooltip se inserta como HTML.
    return mustache.render(TOOLTIP_TEMPLATE, { nombre: entry.nombreLote, sub });
  }

  private _renderLotes = (): void => {
    this._lotesLayerGroup.clearLayers();

    this._state.getAll().forEach((entry) => {
      const layer = L.geoJSON(entry.polygon, {
        style: this._styleFor(entry),
        onEachFeature: (_feature, sublayer) => {
          sublayer.bindTooltip(this._tooltipFor(entry), {
            direction: "center",
            className: "lote-assign-label",
            permanent: true,
          });
          sublayer.on("click", () => this._state.toggleSelection(entry.id));
        },
      });
      layer.addTo(this._lotesLayerGroup);
    });
  };

  private _fitToLotes(): void {
    if (!this._map) return;
    const bounds = this._lotesLayerGroup.getBounds();
    if (bounds.isValid()) this._map.fitBounds(bounds, { padding: [40, 40] });
  }
}
